<template>
  <footer
    v-if="totalItems > 0"
    class="mail-inbox-pager"
    aria-label="Inbox pagination"
  >
    <div class="mail-inbox-pager__bar">
      <button
        type="button"
        class="mail-inbox-pager__arrow"
        aria-label="Previous page"
        :disabled="normalizedCurrentPage <= 1"
        @click="goToPreviousPage"
      >
        <i class="bi bi-chevron-left" aria-hidden="true"></i>
      </button>

      <div class="mail-inbox-pager__center">
        <span class="mail-inbox-pager__page-indicator">
          {{ normalizedCurrentPage }}<span class="mail-inbox-pager__page-sep">/</span>{{ totalPages }}
        </span>
        <span class="mail-inbox-pager__range">
          {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }}
        </span>
      </div>

      <button
        type="button"
        class="mail-inbox-pager__arrow"
        aria-label="Next page"
        :disabled="normalizedCurrentPage >= totalPages"
        @click="goToNextPage"
      >
        <i class="bi bi-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <div
      v-if="totalPages > 1"
      class="mail-inbox-pager__track"
      role="progressbar"
      :aria-valuenow="normalizedCurrentPage"
      :aria-valuemin="1"
      :aria-valuemax="totalPages"
      :aria-label="`Page ${normalizedCurrentPage} of ${totalPages}`"
    >
      <span class="mail-inbox-pager__track-fill" :style="{ width: progressPercent }"></span>
    </div>

    <div v-if="showItemsPerPage" class="mail-inbox-pager__size">
      <span class="mail-inbox-pager__size-label">Show</span>
      <div class="mail-inbox-pager__size-pills" role="group" aria-label="Messages per page">
        <button
          v-for="size in perPageOptions"
          :key="size"
          type="button"
          class="mail-inbox-pager__size-pill"
          :class="{ 'mail-inbox-pager__size-pill--active': size === itemsPerPage }"
          :aria-pressed="size === itemsPerPage ? 'true' : 'false'"
          @click="selectPageSize(size)"
        >
          {{ size }}
        </button>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'MailInboxPagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 25,
    },
    showItemsPerPage: {
      type: Boolean,
      default: true,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
  },
  emits: ['page-change', 'items-per-page-change'],
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
    normalizedCurrentPage() {
      const page = Number(this.currentPage);
      if (!Number.isFinite(page) || page < 1) return 1;
      return Math.min(Math.floor(page), this.totalPages);
    },
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.normalizedCurrentPage - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      return Math.min(this.normalizedCurrentPage * this.itemsPerPage, this.totalItems);
    },
    progressPercent() {
      if (this.totalPages <= 1) return '100%';
      return `${(this.normalizedCurrentPage / this.totalPages) * 100}%`;
    },
  },
  methods: {
    goToPreviousPage() {
      if (this.normalizedCurrentPage > 1) {
        this.$emit('page-change', this.normalizedCurrentPage - 1);
      }
    },
    goToNextPage() {
      if (this.normalizedCurrentPage < this.totalPages) {
        this.$emit('page-change', this.normalizedCurrentPage + 1);
      }
    },
    selectPageSize(size) {
      const parsed = Number(size);
      if (!Number.isNaN(parsed) && parsed !== this.itemsPerPage) {
        this.$emit('items-per-page-change', parsed);
      }
    },
  },
};
</script>

<style src="../styles/mail-inbox-pagination.css"></style>
